import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { motion } from "framer-motion";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [tracker, setTracker] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    (async () => {
      try {
        const p = await api.get("/plans"); setPlans(p.data);
        const t = await api.get("/tracker"); setTracker(t.data?.items || {});
      } catch {}
    })();
  }, [user, loading, nav]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><span className="eyebrow">Loading…</span></div>;
  }

  const doneCount = Object.values(tracker).filter(Boolean).length;

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-dashboard">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">Your dashboard</span>
        </div>
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <h1 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
              Bonjour, <em className="italic text-[#b8892b]">{user.name?.split(" ")[0]}</em>.
            </h1>
            <p className="mt-4 text-lg text-[#4a5570]">Signed in as {user.email} · {user.provider === "google" ? "Google" : "Email"}</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <button onClick={logout} data-testid="dashboard-logout" className="btn-sharp btn-sharp-outline">Sign out</button>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="dashboard-cards">
          <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} className="bg-white border border-[#0d1b3e]/10 p-6">
            <div className="eyebrow">Saved plans</div>
            <div className="serif text-5xl text-[#b8892b] mt-2">{plans.length}</div>
            <Link to="/advisor" className="btn-sharp btn-sharp-outline mt-6 w-full justify-center">New plan</Link>
          </motion.div>
          <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0, transition:{delay:0.05} }} className="bg-white border border-[#0d1b3e]/10 p-6">
            <div className="eyebrow">Tracker progress</div>
            <div className="serif text-5xl text-[#b8892b] mt-2">{doneCount}</div>
            <Link to="/apply" className="btn-sharp btn-sharp-outline mt-6 w-full justify-center">Open tracker</Link>
          </motion.div>
          <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0, transition:{delay:0.1} }} className="bg-[#0d1b3e] text-white p-6">
            <div className="eyebrow text-[#b8892b]">Concierge</div>
            <div className="serif text-2xl mt-2 leading-tight">Ready for a human?</div>
            <Link to="/pricing" className="btn-sharp btn-sharp-gold mt-6 w-full justify-center">Book a session</Link>
          </motion.div>
        </div>

        <div className="mt-16">
          <div className="flex items-center gap-4 border-b border-[#0d1b3e]/15 pb-3 mb-6">
            <h2 className="serif text-3xl text-[#0d1b3e]">Saved plans</h2>
          </div>
          {plans.length === 0 ? (
            <div className="border border-dashed border-[#0d1b3e]/20 p-10 text-center">
              <p className="text-[#4a5570]">No plans yet. <Link to="/advisor" className="text-[#b8892b] underline">Run the advisor</Link> to save your first one.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="dashboard-plans">
              {plans.map((p) => (
                <div key={p.plan_id} className="bg-white border border-[#0d1b3e]/10 p-6">
                  <div className="text-xs text-[#4a5570]">{new Date(p.created_at).toLocaleDateString()}</div>
                  <div className="serif text-xl text-[#0d1b3e] mt-1">{p.intake?.field} · {p.intake?.city}</div>
                  <p className="text-sm text-[#4a5570] mt-2 line-clamp-3">{p.ai_summary}</p>
                  <div className="mt-3 text-xs text-[#b8892b] uppercase tracking-[0.2em]">
                    {p.matches?.length || 0} matches
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
